import { NetworkStatus } from "@apollo/client";
import { Error, UpToTop } from "@carbon/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import ScrollToTop from "react-scroll-to-top";

import PageLoader from "../../components/PageLoader";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import { useHeader } from "../../contexts/PageHeaderContext";
import { useUserContext } from "../../contexts/UserContext";
import useStockSearchQuery from "../../hooks/graphql-hooks/queries/useStockSearchQuery";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  Permissions,
  StockItemFragment,
  StockOverviewPagedFragment,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import "./StockOverview.scss";
import StockSearch, {
  StockSearchValue,
} from "./components/stockSearch/StockSearch";
import StockTable from "./components/stockTable/StockTable";

const pageSize = 20;

interface StocksQueryVariables {
  offset: number;
  limit: number;
  searchText?: string;
}

const initialQueryParameters: StocksQueryVariables = {
  offset: 0,
  limit: pageSize,
};

const StockOverview = () => {
  const { t } = useTranslation();
  const { updateHeader, updateSecondHeader } = useHeader();
  const { user } = useUserContext();
  const [scrolledToBottom, resetScrolledToBottom] = useInfiniteScroll();
  const [stocksPaged, setStocksPaged] = useState<StockOverviewPagedFragment>();
  const [searchValue, setSearchValue] = useState<StockSearchValue>({
    materialName: "",
    materialNumber: "",
  });

  const [stocksQuery, { loading, error, fetchMore, networkStatus }] =
    useStockSearchQuery({
      dataAction: (collection) => {
        setStocksPaged(collection);
      },
    });

  const stocks = useMemo(() => stocksPaged?.data ?? [], [stocksPaged?.data]);

  const buildSearchText = useCallback((value: StockSearchValue) => {
    return [value.materialName, value.materialNumber]
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
      .join(" ");
  }, []);

  const getStocks = useCallback(
    (queryParams: StocksQueryVariables) => {
      stocksQuery({ variables: { input: queryParams } }).catch(() => {});
    },
    [stocksQuery],
  );

  const handleSearch = useCallback(
    (value: StockSearchValue) => {
      setSearchValue(value);

      const searchText = buildSearchText(value);
      getStocks({
        ...initialQueryParameters,
        searchText: searchText || undefined,
      });
    },
    [buildSearchText, getStocks],
  );

  const fetchMoreStocks = useCallback(() => {
    if (!user.permissions.includes(Permissions.CanViewStock)) {
      resetScrolledToBottom();
      return;
    }

    if (!stocksPaged?.pageInfo?.hasNextPage) {
      resetScrolledToBottom();
      return;
    }

    fetchMore({
      variables: {
        input: {
          ...initialQueryParameters,
          offset: stocks.length,
          searchText: buildSearchText(searchValue) || undefined,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const existingData = prev.stocks.data ?? [];
        const incomingData = fetchMoreResult.stocks.data ?? [];

        const merged: StockItemFragment[] = [...existingData];
        incomingData.forEach((entry: StockItemFragment) => {
          const serialKey = (entry.serialNumbers ?? []).join("|");
          const key = `${entry.materialId ?? "none"}-${entry.materialNumber ?? "none"}-${entry.batch ?? "none"}-${entry.baseUnit ?? "none"}-${serialKey}`;

          const alreadyExists = merged.some((candidate: StockItemFragment) => {
            const candidateSerialKey = (candidate.serialNumbers ?? []).join(
              "|",
            );
            const candidateKey = `${candidate.materialId ?? "none"}-${candidate.materialNumber ?? "none"}-${candidate.batch ?? "none"}-${candidate.baseUnit ?? "none"}-${candidateSerialKey}`;
            return candidateKey === key;
          });

          if (!alreadyExists) {
            merged.push(entry);
          }
        });

        return {
          stocks: {
            ...fetchMoreResult.stocks,
            data: merged,
          },
        };
      },
    })
      .catch(() => {})
      .finally(() => resetScrolledToBottom());
  }, [
    buildSearchText,
    fetchMore,
    resetScrolledToBottom,
    searchValue,
    stocks.length,
    stocksPaged?.pageInfo?.hasNextPage,
    user.permissions,
  ]);

  useEffect(() => {
    if (user.permissions.includes(Permissions.CanViewStock)) {
      getStocks(initialQueryParameters);
    }
  }, [getStocks, user.permissions]);

  useEffect(() => {
    updateHeader(<>{t("stockOverview")}</>);
    updateSecondHeader(null);
  }, [t, updateHeader, updateSecondHeader]);

  useEffect(() => {
    if (scrolledToBottom) {
      fetchMoreStocks();
    }
  }, [fetchMoreStocks, scrolledToBottom]);

  const filteredStocks = useMemo(() => {
    const normalizedMaterialName = searchValue.materialName
      .trim()
      .toLowerCase();
    const normalizedMaterialNumber = searchValue.materialNumber
      .trim()
      .toLowerCase();

    return stocks.filter((item) => {
      const stockMaterialName = item.materialName?.toLowerCase() ?? "";
      const stockMaterialNumber = item.materialNumber?.toLowerCase() ?? "";

      const materialNameMatches =
        !normalizedMaterialName ||
        stockMaterialName.includes(normalizedMaterialName);
      const materialNumberMatches =
        !normalizedMaterialNumber ||
        stockMaterialNumber.includes(normalizedMaterialNumber);

      return materialNameMatches && materialNumberMatches;
    });
  }, [searchValue.materialName, searchValue.materialNumber, stocks]);

  if (!user.permissions.includes(Permissions.CanViewStock)) {
    return (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("unauthorized")}
      />
    );
  }

  if (error) {
    return (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("stocksError")}
      />
    );
  }

  const stockTable =
    networkStatus === NetworkStatus.loading ||
    (loading && networkStatus !== NetworkStatus.fetchMore) ? (
      <PageLoader />
    ) : (
      <StockTable items={filteredStocks} />
    );

  return (
    <div className="page-content stock-overview">
      <Helmet title={t("stockOverview")} />
      <StockSearch onSearch={handleSearch} />
      {stockTable}
      {networkStatus === NetworkStatus.fetchMore && (
        <PageLoader className="mt-15" text={t("loadingMoreEntries")} />
      )}
      <ScrollToTop style={{ bottom: "80px" }} smooth component={<UpToTop />} />
    </div>
  );
};

export default StockOverview;
